import { defineStore } from "pinia";
import { Campaign } from "@/types/campaign";
import { APIClient, Response } from "@/utils/httpClient";
import { getRouter } from "@/utils/routerRef";
import { authStore } from "./authStore";
import { customPageStore } from "./customPageStore";
import { customComponentStore } from "./customComponentStore";
import { navigationStore } from "./navigationStore";
import { ActionType } from "@/types/customPage";

const buildErrorRoute = (message: string): string => {
	const params = new URLSearchParams({ message });
	const campaignId = authStore().getCampaignID;
	if (campaignId) {
		params.set("campaign", campaignId);
	}
	return `/error?${params.toString()}`;
};

/**
 * Hydrates customPageStore + customComponentStore from a CAMPAIGN-PAUSED response
 * payload if the backend has included them (parallel backend PR). No-ops silently
 * when the fields are absent (back-compat with older backend responses).
 */
function hydratePausedStores(responseData: any): void {
	if (responseData.customPages && Array.isArray(responseData.customPages)) {
		customPageStore().saveItems(responseData.customPages);
	}
	if (responseData.customComponents && Array.isArray(responseData.customComponents)) {
		customComponentStore().saveItems(responseData.customComponents);
	}
}

interface ICampaignStoreState {
	campaign: Campaign | null;
	deviceId: string | null;
}

export const campaignStore = defineStore("campaignStore", {
	persist: true,
	state: () =>
	({
		campaign: null,
		deviceId: null,
	} as ICampaignStoreState),
	getters: {
		getItem(state: ICampaignStoreState) {
			return state.campaign;
		},
		getDeviceId(state: ICampaignStoreState) {
			return state.deviceId;
		},
	},
	actions: {
		async getInitialCampaignData(campaignSource: {
			campaignId: string | null;
			subdomain: string | null;
		}) {
			const endpoint = `/campaigns/initial`;
			// use the campaignId and subdomain to get the campaign data
			// use the any type for the data object to avoid type errors
			// as the data object can have different keys
			let data: any = {}
			if (campaignSource.subdomain) data["subdomain"] = campaignSource.subdomain;
			if (campaignSource.campaignId) data["campaignId"] = campaignSource.campaignId;
			try {
				const response = await APIClient.post(endpoint, data, {
					headers: { auth_type: "manager" },
				});
				if (response.success) {
					console.log(response.data);
					return response.data;
				}
				if (
					response.data &&
					response.data.status === "CAMPAIGN-PAUSED"
				) {
					const pausedCampaignId =
						response.data.campaignId || campaignSource.campaignId;
					if (pausedCampaignId) {
						hydratePausedStores(response.data);
						navigationStore().emitAction({
							type: ActionType.GO_TO_PAGE,
							value: "campaign-paused",
						});
					}
					return null;
				}
			} catch (error: any) {
				console.error(error);
				if (error instanceof Error) {
					getRouter().push(buildErrorRoute(error.message));
				} else {
					getRouter().push(buildErrorRoute("Unknown error"));
				}
			}
		},
		async getCampaignFromPath(path: string): Promise<Response> {
			const endpoint = `/campaigns/path/${path}`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					this.campaign = response.data;
					return response;
				}
			} catch (error: any) {
				console.error(error);
				if (error instanceof Error) {
					getRouter().push(buildErrorRoute(error.message));
				} else {
					getRouter().push(buildErrorRoute("Unknown error"));
				}
			}
			return { success: false, data: null, message: "No campaign found" };
		},
		setCampaign(item: Campaign): void {
			this.campaign = item;
		},
		setDeviceId(id: string): void {
			this.deviceId = id;
		},
		async getCampaign() {
			const authState = authStore();
			const campaignId = authState.campaignId;
			const endpoint = `/campaigns/${campaignId}`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					this.campaign = response.data;
					return response.data;
				} else {
					if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-ENDED"
					) {
						getRouter().replace(`/${campaignId}/campaign-over`);
					} else if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-PAUSED"
					) {
						hydratePausedStores(response.data);
						navigationStore().emitAction({
							type: ActionType.GO_TO_PAGE,
							value: "campaign-paused",
						});
					} else {
						return null;
					}
				}
			} catch (error: any) {
				console.error(error);
				if (error instanceof Error) {
					getRouter().push(buildErrorRoute(error.message));
				} else {
					getRouter().push(buildErrorRoute("Unknown error"));
				}
			}
		},
		async shareCampaign() {
			const authState = authStore();
			const campaignId = authState.campaignId;
			const userId = authState.userId;
			const endpoint = `/campaigns/${campaignId}/users/${userId}/shared`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					console.log("Campaign Shared");
				} else {
					if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-ENDED"
					) {
						getRouter().replace(`/${campaignId}/campaign-over`);
					} else if (
						response.data &&
						response.data.status &&
						response.data.status == "CAMPAIGN-PAUSED"
					) {
						hydratePausedStores(response.data);
						navigationStore().emitAction({
							type: ActionType.GO_TO_PAGE,
							value: "campaign-paused",
						});
					}
				}
			} catch (error) {
				console.error(error);
			}
		},
	},
});
